/**
 * The Throughput window's two charts (shipcast-o7j.7, Q16): the WHOLE stored
 * history, raw, with what the team's range and cap do to it marked — and the
 * per-day distribution of all history beside the one the simulation uses, so
 * a range or a cap that changes the shape shows as a gap between the two.
 *
 * They preview the draft settings: pass the draft's cut, not the saved one.
 */

import { useMemo } from 'react';
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { dayCountBars, zeroDayShare } from '@/lib/throughputCharts';

import type { ThroughputCut } from '../throughputCut';

const KEPT = 'var(--color-action-primary)';
const CAPPED = 'var(--color-feedback-warning)';
const OUTSIDE = 'var(--color-text-muted)';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

interface RawDay {
  date: string;
  count: number;
  kept: number;
  capped: number;
  outside: number;
  monthLabel: string;
}

export function ThroughputRawCharts({ cut, cap }: { cut: ThroughputCut; cap: number | null }) {
  const raw = useMemo(() => rawDays(cut), [cut]);
  const all = cut.days.map((d) => d.count);

  return (
    <div className="space-y-4">
      <figure>
        <figcaption className="text-xs font-medium text-text-secondary">History, day by day</figcaption>
        <div role="img" aria-label={describeHistory(cut, cap)} className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={raw} barCategoryGap={0} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <XAxis
                dataKey="date"
                tickFormatter={(_d: string, i: number) => raw[i]?.monthLabel ?? ''}
                interval={0}
                tickLine={false}
                tickSize={0}
                axisLine={false}
                tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                height={14}
              />
              <Tooltip cursor={false} content={<RawTooltip />} />
              <Bar dataKey="outside" stackId="day" fill={OUTSIDE} fillOpacity={0.35} isAnimationActive={false} />
              <Bar dataKey="kept" stackId="day" fill={KEPT} isAnimationActive={false} />
              <Bar dataKey="capped" stackId="day" fill={CAPPED} isAnimationActive={false} />
              {cap !== null && <ReferenceLine y={cap} stroke={CAPPED} strokeDasharray="3 3" />}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <Legend
          items={[
            [KEPT, 'used'],
            [OUTSIDE, 'outside the range'],
            ...(cap !== null ? ([[CAPPED, `above the cap of ${cap}`]] as const) : []),
          ]}
        />
      </figure>

      <figure>
        <figcaption className="text-xs font-medium text-text-secondary">Items per day: all history vs used</figcaption>
        <DistributionComparison all={all} used={cut.used} />
      </figure>
    </div>
  );
}

function DistributionComparison({ all, used }: { all: readonly number[]; used: readonly number[] }) {
  const data = useMemo(() => {
    const allBars = dayCountBars(all);
    const usedBars = dayCountBars(used);
    const max = Math.max(allBars.length, usedBars.length);
    // Shares, not counts: the two series hold different numbers of days.
    return Array.from({ length: max }, (_, value) => ({
      value,
      all: share(allBars[value]?.days ?? 0, all.length),
      used: share(usedBars[value]?.days ?? 0, used.length),
    }));
  }, [all, used]);

  return (
    <>
      <div role="img" aria-label={describeComparison(all, used)} className="h-24">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="value"
              tickLine={false}
              tickSize={0}
              axisLine={false}
              tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
              height={14}
            />
            <Tooltip cursor={false} content={<ComparisonTooltip />} />
            <Bar dataKey="all" fill={OUTSIDE} fillOpacity={0.5} isAnimationActive={false} />
            <Bar dataKey="used" fill={KEPT} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <Legend
        items={[
          [OUTSIDE, `all history — ${zeroCaption(all)}`],
          [KEPT, `used — ${zeroCaption(used)}`],
        ]}
      />
    </>
  );
}

function Legend({ items }: { items: ReadonlyArray<readonly [string, string]> }) {
  return (
    <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-muted">
      {items.map(([color, label]) => (
        <li key={label} className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block size-2.5 rounded-xs" style={{ backgroundColor: color }} />
          {label}
        </li>
      ))}
    </ul>
  );
}

function rawDays(cut: ThroughputCut): RawDay[] {
  let previousMonth = -1;
  return cut.days.map((d) => {
    const month = Number(d.date.slice(5, 7)) - 1;
    const monthLabel = month !== previousMonth ? (MONTHS[month] ?? '') : '';
    previousMonth = month;
    return {
      date: d.date,
      count: d.count,
      kept: d.inRange ? d.kept : 0,
      capped: d.inRange ? d.count - d.kept : 0,
      outside: d.inRange ? 0 : d.count,
      monthLabel,
    };
  });
}

function share(days: number, total: number): number {
  return total === 0 ? 0 : Math.round((days / total) * 1000) / 10;
}

function zeroCaption(values: readonly number[]): string {
  return `${Math.round(zeroDayShare(values) * 100)}% of days delivered nothing`;
}

function describeHistory(cut: ThroughputCut, cap: number | null): string {
  const first = cut.days[0]?.date;
  const last = cut.days[cut.days.length - 1]?.date;
  const inRange = cut.days.filter((d) => d.inRange);
  const removed = inRange.reduce((sum, d) => sum + (d.count - d.kept), 0);
  const cappedDays = inRange.filter((d) => d.count > d.kept).length;
  const capText =
    cap === null
      ? ''
      : `; the cap of ${cap} removes ${removed} items on ${cappedDays} day${cappedDays === 1 ? '' : 's'}`;
  return `${cut.days.length} days of history from ${first} to ${last}; the range uses ${inRange.length} days from ${cut.from} to ${cut.to}${capText}`;
}

function describeComparison(all: readonly number[], used: readonly number[]): string {
  return `Items per day. All history: ${all.length} days, ${zeroCaption(all)}. Used: ${used.length} days, ${zeroCaption(used)}.`;
}

function RawTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: RawDay }> }) {
  const day = active ? payload?.[0]?.payload : undefined;
  if (!day) return null;
  const note = day.outside > 0 ? ' (outside the range)' : day.capped > 0 ? ` (${day.kept} after the cap)` : '';
  return (
    <div className="rounded-sm border border-border-default bg-bg-elevated px-2 py-1 text-xs shadow-sm">
      {day.date}: {day.count} item{day.count === 1 ? '' : 's'}
      {note}
    </div>
  );
}

function ComparisonTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { value: number; all: number; used: number } }>;
}) {
  const bar = active ? payload?.[0]?.payload : undefined;
  if (!bar) return null;
  return (
    <div className="rounded-sm border border-border-default bg-bg-elevated px-2 py-1 text-xs shadow-sm">
      {bar.value} item{bar.value === 1 ? '' : 's'} a day: {bar.all}% of all days, {bar.used}% of used days
    </div>
  );
}
